/**
 * UBM Bills Page - View all collected bills
 */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../api";
import { toast } from "sonner";
import {
  FileText,
  Download,
  Search,
  Filter,
  Calendar,
  Building2,
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function UBMBills() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(25);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVendor, setSelectedVendor] = useState("all");
  
  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, bill: null });
  
  useEffect(() => {
    loadData();
  }, [orgId, page, selectedVendor]);
  
  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load vendors for filter
      const vendorsRes = await api.get(`/ubm/${orgId}/vendors`);
      setVendors(vendorsRes.data.vendors || []);
      
      // Load bills
      let url = `/ubm/${orgId}/bills?limit=${limit}&offset=${page * limit}`;
      if (selectedVendor && selectedVendor !== "all") {
        url += `&vendor_id=${selectedVendor}`;
      }
      
      const billsRes = await api.get(url);
      setBills(billsRes.data.bills || []);
      setTotal(billsRes.data.total || 0);
    } catch (error) {
      console.error("Failed to load bills:", error);
      if (error.response?.status === 403) {
        toast.error("UBM module not enabled for this organization");
        navigate(`/org/${orgId}`);
      } else {
        toast.error("Failed to load bills");
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async () => {
    if (!deleteDialog.bill) return;
    
    try {
      await api.delete(`/ubm/${orgId}/bills/${deleteDialog.bill.id}`);
      toast.success("Bill deleted");
      loadData();
    } catch (error) {
      toast.error("Failed to delete bill");
    } finally {
      setDeleteDialog({ open: false, bill: null });
    }
  };
  
  const filteredBills = bills.filter(bill => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      bill.vendor_name?.toLowerCase().includes(query) ||
      bill.account_number?.toLowerCase().includes(query) ||
      bill.bill_number?.toLowerCase().includes(query)
    );
  });
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };
  
  const formatCurrency = (amount) => {
    if (!amount) return "-";
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6" data-testid="ubm-bills-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(`/org/${orgId}`)}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="w-6 h-6 text-blue-400" />
              Bills
            </h1>
            <p className="text-zinc-400 text-sm">View all collected bills</p>
          </div>
        </div>
        
        <Button onClick={loadData} variant="outline" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      
      {/* Filters */}
      <Card className="bg-zinc-900 border-zinc-800 mb-6">
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                placeholder="Search by vendor, account, or bill number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-zinc-800 border-zinc-700"
              />
            </div>
            
            <Select value={selectedVendor} onValueChange={setSelectedVendor}>
              <SelectTrigger className="w-[200px] bg-zinc-800 border-zinc-700">
                <Building2 className="w-4 h-4 mr-2 text-zinc-500" />
                <SelectValue placeholder="All Vendors" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="all">All Vendors</SelectItem>
                {vendors.map(v => (
                  <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Bills Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              {total} Bills
            </CardTitle>
            <div className="flex items-center gap-2 text-sm text-zinc-400">
              Page {page + 1} of {Math.ceil(total / limit) || 1}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
            </div>
          ) : filteredBills.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No bills found</p>
              <p className="text-sm">Bills will appear here after collection</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Vendor</TableHead>
                  <TableHead className="text-zinc-400">Account #</TableHead>
                  <TableHead className="text-zinc-400">Bill Date</TableHead>
                  <TableHead className="text-zinc-400">Due Date</TableHead>
                  <TableHead className="text-zinc-400">Amount</TableHead>
                  <TableHead className="text-zinc-400">Collected</TableHead>
                  <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBills.map((bill) => (
                  <TableRow key={bill.id} className="border-zinc-800">
                    <TableCell className="font-medium">{bill.vendor_name}</TableCell>
                    <TableCell>{bill.account_number || "-"}</TableCell>
                    <TableCell>{formatDate(bill.bill_date)}</TableCell>
                    <TableCell>{formatDate(bill.due_date)}</TableCell>
                    <TableCell className="font-mono">{formatCurrency(bill.amount)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        <Calendar className="w-3 h-3 mr-1" />
                        {formatDate(bill.collected_at)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {bill.file_url && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            onClick={() => window.open(bill.file_url, '_blank')}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            PDF
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          onClick={() => setDeleteDialog({ open: true, bill })}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          
          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-zinc-800">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-zinc-400 px-4">
                Page {page + 1} of {Math.ceil(total / limit)}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * limit >= total}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, bill: null })}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Bill?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this bill record. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
